import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { fmtDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Settings, Shield, Key, Smartphone, CheckCircle2, XCircle, Lock,
  RefreshCw, AlertTriangle, ShieldCheck, Eye, EyeOff, User, Building2, Save, Monitor, Trash2, Copy, Clock, Sprout, ToggleLeft, Tractor, FileCheck, Globe, Coins, TrendingUp, Zap, Percent, Store, CreditCard
} from 'lucide-react';
import ResetCompanyModal from '../components/ResetCompanyModal';
import { TelegramSettingsTab } from '../components/settings/TelegramSettingsTab';
import ShiftsSettingsPanel from '../components/shifts/ShiftsSettingsPanel';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import SubscriptionSettings from '../components/settings/SubscriptionSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── BIR Print Toggle (3-state: auto | ask | disabled) ───────────────────────
function BirPrintToggle({ label, description, testId, value, onChange, hideAsk = false }) {
  const [saving, setSaving] = useState(false);
  const handleChange = async (v) => {
    setSaving(true);
    try {
      await onChange(v);
      toast.success(`${label}: ${v === 'auto' ? 'Auto' : v === 'ask' ? 'Ask before print' : 'Disabled'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };
  const opts = hideAsk
    ? [{ v: 'auto', label: 'Auto', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' }, { v: 'disabled', label: 'Disabled', color: 'bg-slate-100 text-slate-500 border-slate-300' }]
    : [{ v: 'auto', label: 'Auto', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' }, { v: 'ask', label: 'Ask', color: 'bg-amber-100 text-amber-700 border-amber-300' }, { v: 'disabled', label: 'Disabled', color: 'bg-slate-100 text-slate-500 border-slate-300' }];
  const norm = (val) => {
    if (val === true) return 'auto';
    if (val === false) return 'disabled';
    return val || 'auto';
  };
  const current = norm(value);
  return (
    <div data-testid={testId} className="flex items-center justify-between p-3 rounded-lg border bg-white" style={{ borderColor: 'var(--border-default)' }}>
      <div className="flex-1 min-w-0 mr-3">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{description}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {opts.map(opt => (
          <button
            key={opt.v}
            data-testid={`${testId}-${opt.v}-btn`}
            disabled={saving}
            onClick={() => handleChange(opt.v)}
            className={`px-2.5 py-1 rounded-md border text-xs font-semibold transition-all ${current === opt.v ? opt.color : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'}`}
          >
            {saving && current !== opt.v ? null : opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── BIR Document Printing panel (shared) ─────────────────────────────────────
// One panel for the per-branch BIR printing/POS behavior. Lives in the BIR
// Compliance tab (Step 3 of the setup flow); props-driven so it stays wired to
// whichever branch selector hosts it.
function BirPrintingPanel({ bir, savingKey, onToggle }) {
  if (bir?.bir_strict_mode) {
    return (
      <div className="rounded-xl border border-emerald-300 bg-emerald-50/60 p-4 space-y-3" data-testid="branch-strict-mode-lock">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1A4D2E] text-white text-[10px] font-bold">✓</span>
          <p className="font-bold text-sm text-[#1A4D2E]">BIR Strict Mode Active</p>
          <span className="text-[10px] bg-[#1A4D2E] text-white px-2 py-0.5 rounded-full font-semibold">Accredited</span>
        </div>
        <p className="text-xs text-emerald-800">
          This account operates in <strong>full BIR accreditation mode</strong>. All document auto-print settings are locked to <strong>Auto</strong> and cannot be changed.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {[
            { label: 'SI — Sales Invoice', desc: 'Auto-issued for every cash/digital sale' },
            { label: 'CI — Charge Invoice', desc: 'Auto-issued for every credit sale' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-lg border border-emerald-200 p-3">
              <p className="text-xs font-semibold text-[#1A4D2E]">{item.label}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{item.desc}</p>
              <span className="inline-block mt-1.5 text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">AUTO — LOCKED</span>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400">BIR Z-Report will cover 100% of transactions. To change this mode, contact your AgriBooks administrator.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">VAT Override in Sales</p>
      <BirPrintToggle
        label="Allow VAT Override in Sales (/pos)"
        description="When enabled, cashiers can click a product's VAT badge in the order lines to propose a change. A manager PIN is required at checkout to confirm. Disable this for established companies where VAT is fully configured and should not be touched in sales."
        testId="branch-bir-vat-override"
        value={bir?.vat_override_in_sales_enabled !== false ? 'auto' : 'disabled'}
        hideAsk
        onChange={(v) => onToggle({ vat_override_in_sales_enabled: v === 'auto' }, 'vat_override')}
      />
      <p className="text-[11px] text-slate-400">Note: "Auto" = override allowed (needs manager PIN to confirm). "Disabled" = badge is read-only at POS.</p>

      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mt-3">Auto-Print Settings</p>
      <BirPrintToggle
        label="SI — Sales Invoice"
        description="Issued at POS for every paid/cash sale. Principal taxable document."
        testId="branch-bir-si"
        value={bir?.si_auto_print || 'auto'}
        onChange={(v) => onToggle({ si_auto_print: v }, 'si_auto_print')}
      />
      <BirPrintToggle
        label="CI — Charge Invoice"
        description="Issued at POS for credit/utang sales. Principal taxable document."
        testId="branch-bir-ci"
        value={bir?.ci_auto_print || 'auto'}
        onChange={(v) => onToggle({ ci_auto_print: v }, 'ci_auto_print')}
      />

      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide pt-2">Supplementary Documents (Non-Taxable)</p>
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mt-1">
        <p className="text-xs font-semibold text-slate-700">Proof of Payment (PP)</p>
        <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
          Customer payments generate a <strong>Proof of Payment</strong> (PP-####) — a supplementary, non-BIR document (not valid for claim of input tax). Official Receipts (OR) and Collection Receipts (CR) are retired. Only <strong>Sales Invoices</strong> and <strong>Charge Invoices</strong> are filed with BIR.
        </p>
      </div>
      {savingKey && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5 pt-1"><RefreshCw size={11} className="animate-spin" /> Saving…</p>
      )}
    </div>
  );
}

// ── BIR setup stepper (Step 1 Register → 2 Verification → 3 Printing → 4 Reports)
function BirSetupStepper({ status }) {
  const current = status === 'not_applied' ? 1 : status === 'pending_review' ? 2 : 3;
  const steps = [
    { n: 1, label: 'Register' },
    { n: 2, label: 'Verification' },
    { n: 3, label: 'Printing & POS' },
    { n: 4, label: 'Reports' },
  ];
  return (
    <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="bir-setup-stepper">
      {steps.map((s, i) => {
        const done = status === 'active' ? s.n <= 4 : s.n < current;
        const active = status !== 'active' && s.n === current;
        return (
          <React.Fragment key={s.n}>
            {i > 0 && <div className={`h-px w-4 sm:w-6 ${done ? 'bg-[#1A4D2E]' : 'bg-slate-200'}`} />}
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] font-semibold
              ${done ? 'border-[#1A4D2E]/40 bg-[#1A4D2E]/10 text-[#1A4D2E]'
                : active ? 'border-amber-300 bg-amber-50 text-amber-700'
                : 'border-slate-200 bg-white text-slate-400'}`}>
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold
                ${done ? 'bg-[#1A4D2E] text-white' : active ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {done ? '✓' : s.n}
              </span>
              {s.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── My PIN change form ───────────────────────────────────────────────────────
function MyPinForm({ hasExistingPin, pinLabel = 'PIN' }) {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const handleSave = async () => {
    if (newPin !== confirmPin) { toast.error('PINs do not match'); return; }
    if (newPin.length < 4) { toast.error('PIN must be at least 4 digits'); return; }
    if (hasExistingPin && !currentPin) { toast.error('Enter your current PIN'); return; }
    setSaving(true);
    try {
      await api.put('/auth/change-my-pin', { current_pin: currentPin, new_pin: newPin });
      toast.success('Your PIN has been updated');
      setCurrentPin(''); setNewPin(''); setConfirmPin('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change PIN');
    }
    setSaving(false);
  };

  return (
    <div className="max-w-sm space-y-3">
      {hasExistingPin && (
        <div>
          <Label className="text-xs text-slate-500">Current {pinLabel}</Label>
          <Input data-testid="my-current-pin" type="password" autoComplete="one-time-code" data-lpignore="true" value={currentPin}
            onChange={e => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder={`Enter current ${pinLabel}`} className="mt-1" />
        </div>
      )}
      <div>
        <Label className="text-xs text-slate-500">{hasExistingPin ? `New ${pinLabel}` : `Set ${pinLabel}`}</Label>
        <div className="relative mt-1">
          <Input data-testid="my-new-pin" type={showPin ? 'text' : 'password'} value={newPin}
            onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="Enter 4-8 digit PIN" className="pr-10" />
          <button type="button" onClick={() => setShowPin(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            {showPin ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Confirm {pinLabel}</Label>
        <Input data-testid="my-confirm-pin" type="password" autoComplete="one-time-code" data-lpignore="true" value={confirmPin}
          onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
          placeholder={`Re-enter ${pinLabel}`} className="mt-1" />
      </div>
      {newPin && confirmPin && newPin !== confirmPin && <p className="text-xs text-red-500">PINs do not match</p>}
      <Button data-testid="save-my-pin-btn" onClick={handleSave}
        disabled={saving || !newPin || newPin !== confirmPin || (hasExistingPin && !currentPin)}
        className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
        {saving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <Key size={13} className="mr-1.5" />}
        {hasExistingPin ? `Change My ${pinLabel}` : `Set My ${pinLabel}`}
      </Button>
    </div>
  );
}

// ── Change Password form ─────────────────────────────────────────────────────
function ChangePasswordForm() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    if (newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await api.put('/auth/change-password', { current_password: currentPw, new_password: newPw });
      toast.success('Password changed');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    }
    setSaving(false);
  };

  return (
    <div className="max-w-sm space-y-3">
      <div>
        <Label className="text-xs text-slate-500">Current Password</Label>
        <Input data-testid="current-password" type="password" autoComplete="new-password" value={currentPw}
          onChange={e => setCurrentPw(e.target.value)} placeholder="Enter current password" className="mt-1" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">New Password</Label>
        <Input data-testid="new-password" type="password" autoComplete="new-password" value={newPw}
          onChange={e => setNewPw(e.target.value)} placeholder="Min. 6 characters" className="mt-1" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Confirm Password</Label>
        <Input data-testid="confirm-password" type="password" autoComplete="new-password" value={confirmPw}
          onChange={e => setConfirmPw(e.target.value)} placeholder="Re-enter password" className="mt-1" />
      </div>
      {newPw && confirmPw && newPw !== confirmPw && <p className="text-xs text-red-500">Passwords do not match</p>}
      <Button data-testid="save-password-btn" onClick={handleSave}
        disabled={saving || !currentPw || !newPw || newPw !== confirmPw}
        className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
        {saving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <Lock size={13} className="mr-1.5" />}
        Change Password
      </Button>
    </div>
  );
}

// ── Connect Terminal Panel ─────────────────────────────────────────────────
function ConnectTerminalPanel({ branches }) {
  const [code, setCode] = useState('');
  const [branchId, setBranchId] = useState('');
  const [pairing, setPairing] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qrBranchId, setQrBranchId] = useState('');
  const [qrToken, setQrToken] = useState('');
  const [qrGenerating, setQrGenerating] = useState(false);
  const terminalUrl = `${window.location.origin}/terminal`;

  const loadTerminals = async () => {
    setLoading(true);
    try {
      const res = await api.get('/terminal/active');
      setTerminals(res.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadTerminals(); }, []);

  const handlePair = async () => {
    if (!code.trim() || code.trim().length !== 6) { toast.error('Enter the 6-character code from the terminal'); return; }
    if (!branchId) { toast.error('Select a branch'); return; }
    setPairing(true);
    try {
      const res = await api.post('/terminal/pair', { code: code.trim(), branch_id: branchId });
      toast.success(res.data.message);
      setCode('');
      loadTerminals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to pair terminal');
    }
    setPairing(false);
  };

  const disconnectTerminal = async (terminalId) => {
    try {
      await api.post(`/terminal/disconnect/${terminalId}`);
      toast.success('Terminal disconnected');
      loadTerminals();
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const generateQR = async () => {
    if (!qrBranchId) { toast.error('Select a branch first'); return; }
    setQrGenerating(true);
    try {
      const res = await api.post('/terminal/initiate-qr-pairing', { branch_id: qrBranchId });
      setQrToken(res.data.token);
      toast.success(`QR ready for ${res.data.branch_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate QR');
    }
    setQrGenerating(false);
  };

  return (
    <div className="space-y-6">
      <Card style={{ border: "1px solid var(--border-default)" }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Monitor size={16} /> Pair a Terminal
          </CardTitle>
          <p className="text-sm text-slate-500">
            Connect an Android device, tablet, or phone as an AgriSmart Terminal.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Pair via QR Code */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-emerald-50/30 border border-emerald-200/50">
            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3" style={{ fontFamily: 'Manrope' }}>
              <Smartphone size={15} className="text-emerald-600" /> Quick Pair — Scan QR Code
            </h4>
            <p className="text-xs text-slate-500 mb-3">Select a branch, generate a QR code, and scan it with your mobile device to instantly connect.</p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1 block">Branch</Label>
                <select
                  value={qrBranchId}
                  onChange={e => { setQrBranchId(e.target.value); setQrToken(''); }}
                  className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="qr-branch-select"
                >
                  <option value="">Select branch...</option>
                  {(branches || []).map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <Button onClick={generateQR} disabled={qrGenerating || !qrBranchId}
                className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-9 text-xs"
                data-testid="generate-qr-btn">
                {qrGenerating ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <Monitor size={13} className="mr-1.5" />}
                {qrToken ? 'Regenerate' : 'Generate QR'}
              </Button>
            </div>
            {qrToken && (
              <div className="mt-4 flex flex-col items-center gap-3 p-4 bg-white rounded-lg border-[var(--border-default)] shadow-sm">
                <QRCodeSVG
                  value={`${terminalUrl}?pair=${qrToken}`}
                  size={200}
                  level="M"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#1A4D2E"
                />
                <div className="text-center">
                  <p className="text-xs text-slate-500">Scan with your mobile camera to connect</p>
                  <p className="text-[10px] text-slate-400 mt-1 break-all max-w-[280px]">{terminalUrl}?pair={qrToken.slice(0,12)}...</p>
                  <p className="text-[10px] text-emerald-600 mt-1 font-medium">
                    {branches?.find(b => b.id === qrBranchId)?.name || ''} — Expires in 10 minutes
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">or pair manually</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Terminal URL */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Terminal URL</Label>
            <div className="flex items-center gap-2">
              <Input value={terminalUrl} readOnly text-xs bg-slate-50 data-testid="terminal-url" />
              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(terminalUrl); toast.success('Copied!'); }} data-testid="copy-terminal-url">
                <Copy size={14} />
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1">Open this URL on the mobile device, then enter the code shown below.</p>
          </div>

          {/* Pairing code input */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Code from Terminal</Label>
              <Input
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                placeholder="ABC123"
                maxLength={6}
                className="font-mono text-lg tracking-widest text-center uppercase"
                data-testid="terminal-code-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Assign to Branch</Label>
              <select
                value={branchId}
                onChange={e => setBranchId(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="terminal-branch-select"
              >
                <option value="">Select branch...</option>
                {(branches || []).map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handlePair}
                disabled={pairing || !code || !branchId}
                className="bg-[#1A4D2E] hover:bg-[#14532d] text-white w-full"
                data-testid="pair-terminal-btn"
              >
                {pairing ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <Smartphone size={14} className="mr-1.5" />}
                {pairing ? 'Pairing...' : 'Pair Terminal'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Terminals */}
      <Card style={{ border: "1px solid var(--border-default)" }}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
              Active Terminals
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadTerminals} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {terminals.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No active terminals</p>
          ) : (
            <div className="space-y-2">
              {terminals.map(t => (
                <div key={t.terminal_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`terminal-${t.terminal_id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t.branch_name || 'Unknown Branch'}</p>
                    <p className="text-xs text-slate-500">Paired by {t.user_name} · {fmtDate(t.paired_at)}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => disconnectTerminal(t.terminal_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`disconnect-${t.terminal_id}`}>
                    <Trash2 size={14} className="mr-1" /> Disconnect
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── BIR Pending Section with Document Upload ─────────────────────────────────
function PendingBIRSection({ birBranch, withdrawBirApplication }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadDocs = useCallback(async () => {
    try {
      const res = await api.get('/bir/activation/docs', { params: { branch_id: birBranch } });
      setDocs(res.data.documents || []);
    } catch {}
  }, [birBranch]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('branch_id', birBranch);
      const res = await api.post('/bir/activation/upload-doc', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Document uploaded: ${res.data.filename}`);
      loadDocs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
        <RefreshCw size={16} className="text-amber-600 mt-0.5 shrink-0 animate-spin" />
        <div>
          <p className="text-sm font-semibold text-amber-800">Application Under Review</p>
          <p className="text-xs text-amber-700 mt-1">
            Your BIR Compliance application has been submitted and is being reviewed by our team.
            We'll activate your account once documents are verified — usually within 1 business day.
          </p>
        </div>
      </div>
      {/* Document upload */}
      <div className="border rounded-lg p-3" style={{ borderColor: 'var(--border-default)' }}>
        <p className="text-sm font-medium text-slate-700 mb-2">Supporting Documents</p>
        <p className="text-xs text-slate-500 mb-3">Upload your BIR Certificate of Registration, ATP certificate, or other supporting documents. Accepted: PDF, JPG, PNG (max 10 MB).</p>
        {docs.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {docs.map(doc => (
              <div key={doc.doc_id} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded border" style={{ borderColor: 'var(--border-default)' }}>
                <span className="text-slate-600 truncate max-w-[200px]">{doc.filename}</span>
                {doc.download_url && (
                  <a href={doc.download_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline ml-2 shrink-0">
                    View
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleFileChange} />
          <Button variant="outline" size="sm" className="h-8" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="bir-upload-doc-btn">
            {uploading ? <><RefreshCw size={12} className="animate-spin mr-1.5" />Uploading…</> : 'Upload Document'}
          </Button>
          <span className="text-xs text-slate-400">{docs.length} document{docs.length !== 1 ? 's' : ''} uploaded</span>
        </div>
      </div>
      <button onClick={withdrawBirApplication} className="text-xs text-red-500 hover:text-red-700 underline">
        Withdraw application
      </button>
    </div>
  );
}



export default function SettingsPage() {
  const { user: currentUser, refreshUser, branches, currentBranch } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  // ── Reset Company Modal ────────────────────────────────────────────────────
  const [showResetModal, setShowResetModal] = useState(false);

  // ── TOTP ──────────────────────────────────────────────────────────────────
  const [totpStatus, setTotpStatus] = useState({ enabled: false, verified: false });
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpConfirmCode, setTotpConfirmCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpStep, setTotpStep] = useState('idle');

  const loadTotpStatus = useCallback(async () => {
    if (!isAdmin) return;
    try { const res = await api.get('/auth/totp/status'); setTotpStatus(res.data); } catch {}
  }, [isAdmin]);

  useEffect(() => { loadTotpStatus(); }, [loadTotpStatus]);

  const startTotpSetup = async () => {
    setTotpLoading(true);
    try { const res = await api.post('/auth/totp/setup'); setTotpSetup(res.data); setTotpStep('scan'); }
    catch { toast.error('Failed to generate TOTP secret'); }
    setTotpLoading(false);
  };

  const confirmTotpSetup = async () => {
    if (totpConfirmCode.length !== 6) { toast.error('Enter 6-digit code'); return; }
    setTotpLoading(true);
    try {
      const res = await api.post('/auth/totp/verify-setup', { code: totpConfirmCode });
      if (res.data.verified) {
        toast.success('Authenticator app connected!');
        setTotpStep('idle'); setTotpSetup(null); setTotpConfirmCode(''); loadTotpStatus();
      } else { toast.error(res.data.error || 'Code mismatch'); }
    } catch { toast.error('Verification failed'); }
    setTotpLoading(false);
  };

  const disableTotp = async () => {
    if (!window.confirm('Disable TOTP?')) return;
    try { await api.delete('/auth/totp/disable'); toast.success('TOTP disabled'); loadTotpStatus(); }
    catch { toast.error('Failed'); }
  };

  // ── PIN Policies ──────────────────────────────────────────────────────────
  const [pinPolicyActions, setPinPolicyActions] = useState([]);
  const [pinMethods, setPinMethods] = useState([]);
  const [pinPolicies, setPinPolicies] = useState({});
  const [savingPolicies, setSavingPolicies] = useState(false);

  // Also keep old TOTP state for backward compat
  const [totpActions, setTotpActions] = useState([]);
  const [enabledActions, setEnabledActions] = useState([]);
  const [savingControls, setSavingControls] = useState(false);

  const loadPinPolicies = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/settings/pin-policies');
      setPinPolicyActions(res.data.actions || []);
      setPinMethods(res.data.methods || []);
      setPinPolicies(res.data.policies || {});
    } catch {}
  }, [isAdmin]);

  useEffect(() => { loadPinPolicies(); }, [loadPinPolicies]);

  const togglePinMethod = (actionKey, method) => {
    setPinPolicies(prev => {
      const current = prev[actionKey] || pinPolicyActions.find(a => a.key === actionKey)?.defaults || [];
      const updated = current.includes(method)
        ? current.filter(m => m !== method)
        : [...current, method];
      if (updated.length === 0) { toast.error('At least one method must be enabled'); return prev; }
      return { ...prev, [actionKey]: updated };
    });
  };

  const savePinPolicies = async () => {
    setSavingPolicies(true);
    try {
      await api.put('/settings/pin-policies', { policies: pinPolicies });
      toast.success('PIN policies saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSavingPolicies(false);
  };

  const policyModules = pinPolicyActions.reduce((acc, a) => {
    if (!acc[a.module]) acc[a.module] = [];
    acc[a.module].push(a);
    return acc;
  }, {});

  const METHOD_LABELS = {
    admin_pin:   { label: 'Owner PIN',    color: 'bg-red-100 text-red-700 border-red-200' },
    manager_pin: { label: 'Manager PIN',  color: 'bg-blue-100 text-blue-700 border-blue-200' },
    totp:        { label: 'TOTP',         color: 'bg-purple-100 text-purple-700 border-purple-200' },
    auditor_pin: { label: 'Auditor PIN',  color: 'bg-amber-100 text-amber-700 border-amber-200' },
    staff_pin:   { label: 'Staff PIN',    color: 'bg-teal-100 text-teal-700 border-teal-200' },
  };

  const loadTotpControls = useCallback(async () => {
    if (!isAdmin) return;
    try { const res = await api.get('/settings/totp-controls'); setTotpActions(res.data.actions || []); setEnabledActions(res.data.enabled_actions || []); } catch {}
  }, [isAdmin]);

  useEffect(() => { loadTotpControls(); }, [loadTotpControls]);

  const toggleAction = (key) => setEnabledActions(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const saveControls = async () => {
    setSavingControls(true);
    try { await api.put('/settings/totp-controls', { enabled_actions: enabledActions }); toast.success('TOTP controls saved'); }
    catch { toast.error('Failed to save'); }
    setSavingControls(false);
  };

  const actionsByModule = totpActions.reduce((acc, a) => { if (!acc[a.module]) acc[a.module] = []; acc[a.module].push(a); return acc; }, {});

  // ── Admin PIN ──────────────────────────────────────────────────────────────
  const [auditPinConfigured, setAuditPinConfigured] = useState(false);
  const [newAuditPin, setNewAuditPin] = useState('');
  const [confirmAuditPin, setConfirmAuditPin] = useState('');
  const [showAuditPin, setShowAuditPin] = useState(false);
  const [savingAuditPin, setSavingAuditPin] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.get(`${BACKEND_URL}/api/verify/admin-pin/status`)
        .then(r => setAuditPinConfigured(r.data.configured)).catch(() => {});
    }
  }, [isAdmin]); // eslint-disable-line

  const saveAuditPin = async () => {
    if (newAuditPin.length < 4) { toast.error('PIN must be at least 4 digits'); return; }
    if (newAuditPin !== confirmAuditPin) { toast.error('PINs do not match'); return; }
    setSavingAuditPin(true);
    try {
      await api.post(`${BACKEND_URL}/api/verify/admin-pin/set`, { pin: newAuditPin });
      toast.success('Admin PIN saved');
      setNewAuditPin(''); setConfirmAuditPin(''); setAuditPinConfigured(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingAuditPin(false);
  };

  // ── Auditor Access ────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [auditorEdits, setAuditorEdits] = useState({});
  const [savingAuditor, setSavingAuditor] = useState({});

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    try { const res = await api.get('/users'); setUsers(res.data); } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const getAuditorState = (u) => auditorEdits[u.id] !== undefined ? auditorEdits[u.id] : { is_auditor: u.is_auditor || false, auditor_pin: u.auditor_pin || '' };

  const updateAuditorEdit = (userId, field, value) => {
    setAuditorEdits(prev => ({
      ...prev,
      [userId]: { ...getAuditorState(users.find(u => u.id === userId) || {}), [field]: value },
    }));
  };

  const saveAuditorAccess = async (userId) => {
    const edit = auditorEdits[userId];
    if (!edit) return;
    if (edit.is_auditor && edit.auditor_pin && edit.auditor_pin.length < 4) { toast.error('PIN must be at least 4 digits'); return; }
    setSavingAuditor(prev => ({ ...prev, [userId]: true }));
    try {
      await api.put(`/users/${userId}`, { is_auditor: edit.is_auditor, auditor_pin: edit.is_auditor ? (edit.auditor_pin || undefined) : null });
      toast.success('Auditor access updated'); fetchUsers();
      setAuditorEdits(prev => { const n = { ...prev }; delete n[userId]; return n; });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingAuditor(prev => ({ ...prev, [userId]: false }));
  };

  // ── Profile Edit ───────────────────────────────────────────────────────────
  const [profileForm, setProfileForm] = useState({ full_name: '', email: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setProfileForm({ full_name: currentUser.full_name || '', email: currentUser.email || '' });
    }
  }, [currentUser]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/auth/update-profile', profileForm);
      toast.success('Profile updated');
      refreshUser?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingProfile(false);
  };

  // ── Business Info ──────────────────────────────────────────────────────────
  const [bizInfo, setBizInfo] = useState({
    business_name: '', address: '', phone: '', tin: '', email: '',
    trust_receipt_terms: '', receipt_footer: 'This is not an official receipt.', thermal_width: '58mm',
    logo_url: '', copy_color_guide: '', tax_disclaimer: '', receipt_format: 'dot_matrix',
  });
  const [savingBiz, setSavingBiz] = useState(false);
  const [bizLoaded, setBizLoaded] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const loadBizInfo = useCallback(async () => {
    try {
      const res = await api.get('/settings/business-info');
      setBizInfo(res.data);
      setBizLoaded(true);
    } catch {}
  }, []);

  useEffect(() => { if (isAdmin) loadBizInfo(); }, [isAdmin, loadBizInfo]);

  // ── Pricing & Capital config (capital costing method) ──────────────────────
  const [salesConfig, setSalesConfig] = useState({ capital_costing_method: 'smart' });
  const [savingSalesConfig, setSavingSalesConfig] = useState(false);

  const loadSalesConfig = useCallback(async () => {
    try {
      const res = await api.get('/settings/sales-config');
      setSalesConfig(res.data);
    } catch {}
  }, []);

  useEffect(() => { if (isAdmin) loadSalesConfig(); }, [isAdmin, loadSalesConfig]);

  // ── Branch Settings tab: platform tax config + per-branch toggles ──────────
  const [taxConfig, setTaxConfig] = useState({ vat_rate: 12, scpwd_discount_percent: 20 });
  const [branchSel, setBranchSel] = useState('');
  const [branchBir, setBranchBir] = useState(null);
  const [branchBirLoading, setBranchBirLoading] = useState(false);
  const [savingScpwd, setSavingScpwd] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/settings/tax-config').then(r => setTaxConfig(r.data)).catch(() => {});
  }, [isAdmin]);

  // Default the selected branch to the active branch (or first branch).
  useEffect(() => {
    if (!isAdmin) return;
    const fallback = currentBranch?.id || (branches?.[0]?.id || '');
    setBranchSel(prev => prev || fallback);
  }, [isAdmin, currentBranch, branches]);

  const loadBranchBir = useCallback(async (bid) => {
    if (!bid) return;
    setBranchBirLoading(true);
    try {
      const r = await api.get('/bir/settings', { params: { branch_id: bid } });
      setBranchBir(r.data);
    } catch { setBranchBir(null); }
    setBranchBirLoading(false);
  }, []);

  useEffect(() => { if (isAdmin && branchSel) loadBranchBir(branchSel); }, [isAdmin, branchSel, loadBranchBir]);

  const saveScpwdEnabled = async (enabled) => {
    const prev = !!branchBir?.senior_citizen_discount_enabled;
    setBranchBir(b => ({ ...(b || {}), senior_citizen_discount_enabled: enabled }));
    setSavingScpwd(true);
    try {
      await api.put('/bir/branch-toggles', { branch_id: branchSel, senior_citizen_discount_enabled: enabled });
      toast.success(enabled ? 'SC/PWD discount enabled for this branch' : 'SC/PWD discount disabled for this branch');
    } catch (e) {
      setBranchBir(b => ({ ...(b || {}), senior_citizen_discount_enabled: prev }));
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setSavingScpwd(false);
  };

  const branchSelName = (branches || []).find(b => b.id === branchSel)?.name || currentBranch?.name || '—';

  // Safe per-branch operational toggle save (auto-print / VAT override) — partial $set.
  const [savingBranchToggle, setSavingBranchToggle] = useState('');
  const saveBranchToggle = async (patch, key) => {
    const prevDoc = branchBir;
    setBranchBir(b => ({ ...(b || {}), ...patch }));
    setSavingBranchToggle(key);
    try {
      const res = await api.put('/bir/branch-toggles', { branch_id: branchSel, ...patch });
      setBranchBir(b => ({ ...(b || {}), ...res.data }));
      toast.success('Branch setting saved');
    } catch (e) {
      setBranchBir(prevDoc);
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setSavingBranchToggle('');
  };

  const saveCapitalMethod = async (method) => {
    const prev = salesConfig.capital_costing_method;
    setSalesConfig(c => ({ ...c, capital_costing_method: method }));
    setSavingSalesConfig(true);
    try {
      await api.put('/settings/sales-config', { ...salesConfig, capital_costing_method: method });
      toast.success('Capital costing method updated');
    } catch (e) {
      setSalesConfig(c => ({ ...c, capital_costing_method: prev }));
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setSavingSalesConfig(false);
  };

  const saveVatEnabled = async (enabled) => {
    const prev = salesConfig.vat_enabled !== false;
    setSalesConfig(c => ({ ...c, vat_enabled: enabled }));
    try {
      await api.put('/settings/sales-config', { ...salesConfig, vat_enabled: enabled });
      toast.success(enabled ? 'VAT computation enabled' : 'VAT computation disabled');
    } catch (e) {
      setSalesConfig(c => ({ ...c, vat_enabled: prev }));
      toast.error(e.response?.data?.detail || 'Failed to update VAT setting');
    }
  };

  const saveCapitalPropagation = async (enabled) => {
    const prev = salesConfig.cross_branch_capital_propagation_enabled !== false;
    setSalesConfig(c => ({ ...c, cross_branch_capital_propagation_enabled: enabled }));
    try {
      await api.put('/settings/sales-config', { ...salesConfig, cross_branch_capital_propagation_enabled: enabled });
      toast.success(enabled ? 'Cross-branch capital propagation enabled' : 'Cross-branch capital propagation disabled');
    } catch (e) {
      setSalesConfig(c => ({ ...c, cross_branch_capital_propagation_enabled: prev }));
      toast.error(e.response?.data?.detail || 'Failed to update setting');
    }
  };

  const saveBizInfo = async () => {
    if (!bizInfo.business_name?.trim()) { toast.error('Business name is required'); return; }
    setSavingBiz(true);
    try {
      await api.put('/settings/business-info', bizInfo);
      toast.success('Business info saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingBiz(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/settings/upload-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBizInfo(b => ({ ...b, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploadingLogo(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleLogoRemove = async () => {
    try {
      await api.delete('/settings/logo');
      setBizInfo(b => ({ ...b, logo_url: '' }));
      toast.success('Logo removed');
    } catch (e) { toast.error('Failed to remove logo'); }
  };

  // ── Organization Timezone ──────────────────────────────────────────────────
  // Drives the close-reminder SMS scheduler + the default sale date picker.
  // Changing the TZ takes effect on the next scheduler tick (≤1 minute) and
  // immediately for the Sales date, no restart required.
  const [tzValue, setTzValue] = useState('Asia/Manila');
  const [tzChoices, setTzChoices] = useState([]);
  const [tzDetected, setTzDetected] = useState('');
  const [savingTz, setSavingTz] = useState(false);

  const loadTimezone = useCallback(async () => {
    try {
      const r = await api.get('/settings/timezone');
      setTzValue(r.data.timezone || 'Asia/Manila');
      setTzChoices(r.data.choices || []);
    } catch {}
    try {
      // Browser's resolved IANA zone — used for the "Your device is in X"
      // hint so the admin can match their organization TZ at a glance.
      const dev = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (dev) setTzDetected(dev);
    } catch {}
  }, []);
  useEffect(() => { if (isAdmin) loadTimezone(); }, [isAdmin, loadTimezone]);

  const saveTimezone = async () => {
    setSavingTz(true);
    try {
      await api.put('/settings/timezone', { timezone: tzValue });
      toast.success(`Timezone saved: ${tzValue}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save timezone'); }
    setSavingTz(false);
  };

  // Preview the wall-clock time in the selected TZ so the admin can sanity-
  // check their pick right next to the dropdown. Re-renders each minute.
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setClockTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const tzPreview = (() => {
    try {
      // `clockTick` referenced so this re-runs when the interval fires
      void clockTick;
      return new Intl.DateTimeFormat('en-US', {
        timeZone: tzValue,
        hour: 'numeric', minute: '2-digit', hour12: true,
        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
      }).format(new Date());
    } catch { return '—'; }
  })();

  // ── Feature Flags ──────────────────────────────────────────────────────────
  const [featureFlags, setFeatureFlags] = useState({ crop_credit_enabled: true, farm_expense_enabled: true });
  const [savingFlags, setSavingFlags] = useState(false);

  // ── BIR Activation (4-state machine) ──────────────────────────────────────
  const [birBranch, setBirBranch] = useState('');
  const [birStatus, setBirStatus] = useState({ bir_activation_status: 'not_applied', bir_mode_enabled: false });

  // Per-branch BIR ops settings shown in the BIR tab (Step 3 — printing/POS).
  // Separate from branchBir (Branch Settings tab) because each tab has its own
  // branch selector.
  const [birOps, setBirOps] = useState(null);
  const [birOpsSavingKey, setBirOpsSavingKey] = useState('');
  useEffect(() => {
    if (!isAdmin || !birBranch) { setBirOps(null); return; }
    api.get('/bir/settings', { params: { branch_id: birBranch } })
      .then(r => setBirOps(r.data))
      .catch(() => setBirOps(null));
  }, [isAdmin, birBranch]);
  const saveBirOpsToggle = async (patch, key) => {
    const prevDoc = birOps;
    setBirOps(b => ({ ...(b || {}), ...patch }));
    setBirOpsSavingKey(key);
    try {
      const res = await api.put('/bir/branch-toggles', { branch_id: birBranch, ...patch });
      setBirOps(b => ({ ...(b || {}), ...res.data }));
      // Keep the Branch Settings tab copy in sync if it points at the same branch
      if (birBranch === branchSel) setBranchBir(b => ({ ...(b || {}), ...res.data }));
    } catch (e) {
      setBirOps(prevDoc);
      toast.error(e.response?.data?.detail || 'Failed to update');
      throw e;
    } finally {
      setBirOpsSavingKey('');
    }
  };
  const [activeSettingsTab, setActiveSettingsTab] = useState('account');
  const [showBirApplyForm, setShowBirApplyForm] = useState(false);
  const [birApplyForm, setBirApplyForm] = useState({
    declared_business_name: '', declared_tin: '', declared_vat_type: 'non_vat',
    declared_atp_number: '', declared_machine_serial: '', declared_bir_permit_number: '',
    declared_receipt_workflow: 'staged', senior_citizen_discount_enabled: false, applicant_notes: '',
  });
  const [submittingBir, setSubmittingBir] = useState(false);
  const [birLoadingBranch, setBirLoadingBranch] = useState(false);

  const loadBirStatus = useCallback(async (bid) => {
    if (!bid) return;
    setBirLoadingBranch(true);
    try {
      const res = await api.get('/bir/activation/status', { params: { branch_id: bid } });
      setBirStatus(res.data);
    } catch {}
    setBirLoadingBranch(false);
  }, []);

  useEffect(() => {
    if (isAdmin && branches?.length) {
      const first = branches[0].id;
      setBirBranch(first);
      loadBirStatus(first);
    }
  }, [isAdmin, branches, loadBirStatus]);

  const submitBirApplication = async () => {
    setSubmittingBir(true);
    try {
      const res = await api.post('/bir/activation/apply', { ...birApplyForm, branch_id: birBranch });
      setBirStatus(prev => ({ ...prev, bir_activation_status: res.data.status }));
      setShowBirApplyForm(false);
      if (res.data.auto_approved) {
        toast.success('BIR Compliance activated! Issue Invoice is now available in Sales History.');
        loadBirStatus(birBranch);
      } else {
        toast.success('Application submitted. Our team will review shortly.');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit application'); }
    setSubmittingBir(false);
  };

  const withdrawBirApplication = async () => {
    try {
      await api.delete('/bir/activation/withdraw', { params: { branch_id: birBranch } });
      setBirStatus(prev => ({ ...prev, bir_activation_status: 'not_applied' }));
      toast.success('Application withdrawn.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const loadFeatureFlags = useCallback(async () => {
    try {
      const res = await api.get('/settings/feature-flags');
      setFeatureFlags(res.data);
    } catch {}
  }, []);

  useEffect(() => { if (isAdmin) loadFeatureFlags(); }, [isAdmin, loadFeatureFlags]);

  const toggleFeatureFlag = async (key, value) => {
    const updated = { ...featureFlags, [key]: value };
    setFeatureFlags(updated);
    setSavingFlags(true);
    try {
      await api.put('/settings/feature-flags', updated);
      toast.success(`Feature ${value ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
      setFeatureFlags(prev => ({ ...prev, [key]: !value })); // revert
    }
    setSavingFlags(false);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div>
        <h1 className="text-[24px] font-bold tracking-tight" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isAdmin ? 'Your account, security & system settings' : 'Your account settings'}
        </p>
      </div>

      <Tabs value={activeSettingsTab} onValueChange={setActiveSettingsTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="account" data-testid="account-tab" className="flex items-center gap-1.5">
            <User size={14} /> My Account
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="security" data-testid="security-tab" className="flex items-center gap-1.5">
              <Shield size={14} /> Security
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="business" data-testid="business-tab" className="flex items-center gap-1.5">
              <Building2 size={14} /> Business Info
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="branch" data-testid="branch-settings-tab" className="flex items-center gap-1.5">
              <Store size={14} /> Branch Settings
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="subscription" data-testid="subscription-tab" className="flex items-center gap-1.5">
              <CreditCard size={14} /> Subscription
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="terminal" data-testid="terminal-tab" className="flex items-center gap-1.5">
              <Monitor size={14} /> Connect Terminal
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="telegram" data-testid="telegram-tab" className="flex items-center gap-1.5">
              <Send size={14} /> Telegram
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="shifts" data-testid="shifts-tab" className="flex items-center gap-1.5">
              <Clock size={14} /> Shifts
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── My Account Tab ─────────────────────────────────────────── */}
        <TabsContent value="account" className="space-y-6">
          {/* Profile Info */}
          <Card style={{ border: "1px solid var(--border-default)" }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <User size={18} className="text-[#1A4D2E]" /> Profile
              </CardTitle>
              <p className="text-sm text-slate-500">Update your display name and email</p>
            </CardHeader>
            <CardContent>
              <div className="max-w-sm space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Full Name</Label>
                  <Input data-testid="profile-fullname" value={profileForm.full_name}
                    onChange={e => setProfileForm(p => ({ ...p, full_name: e.target.value }))}
                    placeholder="Your name" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input data-testid="profile-email" type="email" value={profileForm.email}
                    onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="you@example.com" className="mt-1" />
                </div>
                <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={savingProfile}
                  className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                  {savingProfile ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <CheckCircle2 size={13} className="mr-1.5" />}
                  Save Profile
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card style={{ border: "1px solid var(--border-default)" }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Lock size={18} className="text-[#1A4D2E]" /> Change Password
              </CardTitle>
            </CardHeader>
            <CardContent><ChangePasswordForm /></CardContent>
          </Card>

          {/* My PIN — all roles that support PIN auth */}
          {(() => {
            const staffRoles = ['cashier', 'inventory', 'inventory_clerk', 'staff'];
            const managerRoles = ['admin', 'manager'];
            const isStaff = staffRoles.includes(currentUser?.role);
            const isManager = managerRoles.includes(currentUser?.role);
            if (!isStaff && !isManager) return null;
            const pinLabel = isStaff ? 'Staff PIN' : 'Manager PIN';
            const hasPin = isStaff ? currentUser?.has_staff_pin : currentUser?.has_manager_pin;
            const pinDesc = isStaff
              ? 'Your personal PIN used by your manager to authorize stock releases on your behalf.'
              : 'Your personal PIN for verifying transactions and approving actions.';
            return (
              <Card style={{ border: "1px solid var(--border-default)" }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Key size={18} className={isStaff ? 'text-teal-600' : 'text-blue-600'} /> My {pinLabel}
                    {hasPin
                      ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700 ml-2">Set</Badge>
                      : <Badge className="text-[10px] bg-amber-100 text-amber-700 ml-2">Not Set</Badge>
                    }
                  </CardTitle>
                  <p className="text-sm text-slate-500">{pinDesc}</p>
                </CardHeader>
                <CardContent><MyPinForm hasExistingPin={!!hasPin} pinLabel={pinLabel} /></CardContent>
              </Card>
            );
          })()}
        </TabsContent>

        {/* ── Security Tab (Admin Only) ──────────────────────────────── */}
        {isAdmin && (
          <TabsContent value="security" className="space-y-6">

            {/* Admin PIN */}
            <Card style={{ border: "1px solid var(--border-default)" }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <ShieldCheck size={18} className="text-[#1A4D2E]" /> Admin PIN
                  {auditPinConfigured
                    ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700 ml-2">Configured</Badge>
                    : <Badge className="text-[10px] bg-amber-100 text-amber-700 ml-2">Not Set</Badge>}
                </CardTitle>
                <p className="text-sm text-slate-500">
                  A private PIN only you know. Used for sensitive actions like inventory corrections and price edits.
                </p>
              </CardHeader>
              <CardContent>
                <div className="max-w-sm space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">{auditPinConfigured ? 'New PIN' : 'Set PIN'}</Label>
                    <div className="relative mt-1">
                      <Input data-testid="audit-pin-input" type={showAuditPin ? 'text' : 'password'} value={newAuditPin}
                        onChange={e => setNewAuditPin(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="Enter 4-8 digit PIN" className="pr-10" />
                      <button type="button" onClick={() => setShowAuditPin(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        {showAuditPin ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Confirm PIN</Label>
                    <Input data-testid="audit-pin-confirm" type="password" autoComplete="one-time-code" data-lpignore="true" value={confirmAuditPin}
                      onChange={e => setConfirmAuditPin(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="Re-enter PIN" className="mt-1" />
                  </div>
                  {newAuditPin && confirmAuditPin && newAuditPin !== confirmAuditPin && <p className="text-xs text-red-500">PINs do not match</p>}
                  <Button data-testid="save-audit-pin-btn" onClick={saveAuditPin}
                    disabled={savingAuditPin || !newAuditPin || newAuditPin !== confirmAuditPin}
                    className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                    {savingAuditPin ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <ShieldCheck size={13} className="mr-1.5" />}
                    {auditPinConfigured ? 'Update Admin PIN' : 'Set Admin PIN'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* TOTP Setup */}
            <Card style={{ border: "1px solid var(--border-default)" }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Smartphone size={18} className="text-[#1A4D2E]" /> Authenticator App (TOTP)
                  {totpStatus.enabled && totpStatus.verified
                    ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700 ml-2">Active</Badge>
                    : <Badge className="text-[10px] bg-slate-100 text-slate-500 ml-2">Not Set Up</Badge>}
                </CardTitle>
                <p className="text-sm text-slate-500">
                  For remote approvals. Workers call you and you read the 6-digit code from your phone.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {totpStep === 'idle' && (
                  <>
                    {totpStatus.enabled && totpStatus.verified ? (
                      <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 size={20} className="text-emerald-600" />
                          <div>
                            <p className="font-semibold text-emerald-800 text-sm">TOTP is active</p>
                            <p className="text-xs text-emerald-600">Uses your authenticator app codes</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={startTotpSetup} disabled={totpLoading}>
                            <RefreshCw size={13} className="mr-1" /> Regenerate
                          </Button>
                          <Button size="sm" variant="outline" onClick={disableTotp} className="text-red-500 border-red-200 hover:bg-red-50">
                            <XCircle size={13} className="mr-1" /> Disable
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <AlertTriangle size={20} className="text-amber-600" />
                          <div>
                            <p className="font-semibold text-amber-800 text-sm">TOTP not configured</p>
                            <p className="text-xs text-amber-600">Falls back to login password</p>
                          </div>
                        </div>
                        <Button size="sm" onClick={startTotpSetup} disabled={totpLoading} className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                          {totpLoading ? <RefreshCw size={13} className="animate-spin mr-1" /> : <Smartphone size={13} className="mr-1" />}
                          Set Up Now
                        </Button>
                      </div>
                    )}
                  </>
                )}
                {totpStep === 'scan' && totpSetup && (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      <strong>Step 1:</strong> Scan the QR code with your authenticator app.
                    </div>
                    <div className="flex flex-col items-center gap-4 py-4">
                      <div className="p-4 bg-white border-2 border-slate-200 rounded-xl shadow-sm">
                        <QRCodeSVG value={totpSetup.qr_uri} size={180} level="M" />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500 mb-1">Can&apos;t scan? Enter manually:</p>
                        <code className="text-sm bg-slate-100 px-3 py-1.5 rounded tracking-wider select-all">{totpSetup.secret}</code>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => { setTotpStep('idle'); setTotpSetup(null); }}>Cancel</Button>
                      <Button onClick={() => setTotpStep('verify')} className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">Next</Button>
                    </div>
                  </div>
                )}
                {totpStep === 'verify' && (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      <strong>Step 2:</strong> Enter the 6-digit code from your app.
                    </div>
                    <div className="max-w-xs mx-auto space-y-2">
                      <Label>Code</Label>
                      <Input data-testid="totp-confirm-input" value={totpConfirmCode}
                        onChange={e => setTotpConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000" className="text-center text-2xl tracking-[0.4em] font-mono h-12"
                        maxLength={6} autoFocus onKeyDown={e => e.key === 'Enter' && confirmTotpSetup()} />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setTotpStep('scan')}>Back</Button>
                      <Button data-testid="totp-confirm-btn" onClick={confirmTotpSetup}
                        disabled={totpLoading || totpConfirmCode.length !== 6}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        {totpLoading ? <RefreshCw size={13} className="animate-spin mr-1" /> : <CheckCircle2 size={13} className="mr-1" />}
                        Activate
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PIN Policies */}
            <Card style={{ border: "1px solid var(--border-default)" }} data-testid="pin-policies-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Shield size={18} className="text-[#1A4D2E]" /> PIN Policies
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-0.5">Configure which PIN types are accepted for each action.</p>
                  </div>
                  <Button data-testid="save-pin-policies-btn" size="sm" onClick={savePinPolicies} disabled={savingPolicies}
                    className="bg-[#1A4D2E] hover:bg-[#14532d] text-white shrink-0">
                    {savingPolicies ? <RefreshCw size={13} className="animate-spin mr-1" /> : null} Save Policies
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Method Legend */}
                <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-slate-100">
                  {pinMethods.map(m => (
                    <span key={m} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border ${METHOD_LABELS[m]?.color || 'bg-slate-100 text-slate-600'}`}>
                      {METHOD_LABELS[m]?.label || m}
                    </span>
                  ))}
                </div>

                <div className="space-y-5">
                  {Object.entries(policyModules).map(([module, actions]) => (
                    <div key={module}>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{module}</p>
                      <div className="space-y-2">
                        {actions.map(action => {
                          const activeMethods = pinPolicies[action.key] || action.defaults;
                          return (
                            <div key={action.key} data-testid={`pin-policy-${action.key}`}
                              className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition-colors">
                              <div className="min-w-0 mr-3">
                                <p className="text-sm font-medium text-slate-800 truncate">{action.label}</p>
                              </div>
                              <div className="flex gap-1.5 shrink-0">
                                {pinMethods.map(method => {
                                  const isActive = activeMethods.includes(method);
                                  const meta = METHOD_LABELS[method] || {};
                                  return (
                                    <button
                                      key={method}
                                      data-testid={`pin-policy-${action.key}-${method}`}
                                      onClick={() => togglePinMethod(action.key, method)}
                                      className={`text-[10px] font-medium px-2 py-1 rounded-md border transition-all ${
                                        isActive
                                          ? meta.color
                                          : 'bg-white text-slate-300 border-slate-200 hover:border-slate-300'
                                      }`}
                                    >
                                      {meta.label || method}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {pinPolicyActions.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Loading...</p>}
                </div>
              </CardContent>
            </Card>

            {/* Auditor Access */}
            <Card style={{ border: "1px solid var(--border-default)" }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Shield size={18} className="text-amber-600" /> Auditor Access
                </CardTitle>
                <p className="text-sm text-slate-500">Grant auditor access so users can verify with their own PIN.</p>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: "#F1F0ED" }}>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">User</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Auditor</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Auditor PIN</TableHead>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => {
                      const state = getAuditorState(u);
                      const isDirty = auditorEdits[u.id] !== undefined;
                      return (
                        <TableRow key={u.id}>
                          <TableCell>
                            <p className="font-medium text-sm">{u.full_name || u.email}</p>
                            <p className="text-xs text-slate-400">{u.email || u.username} &middot; {u.role}</p>
                          </TableCell>
                          <TableCell>
                            <button onClick={() => updateAuditorEdit(u.id, 'is_auditor', !state.is_auditor)}
                              className={`w-10 h-5 rounded-full relative transition-colors ${state.is_auditor ? 'bg-[#1A4D2E]' : 'bg-slate-200'}`}>
                              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${state.is_auditor ? 'translate-x-5' : 'translate-x-0.5'}`} />
                            </button>
                          </TableCell>
                          <TableCell>
                            {state.is_auditor && (
                              <Input type="password" autoComplete="one-time-code" data-lpignore="true" value={state.auditor_pin}
                                onChange={e => updateAuditorEdit(u.id, 'auditor_pin', e.target.value.replace(/\D/g, '').slice(0, 8))}
                                placeholder="4-8 digits" className="h-8 w-28 text-sm" />
                            )}
                          </TableCell>
                          <TableCell>
                            {isDirty && (
                              <Button size="sm" onClick={() => saveAuditorAccess(u.id)} disabled={savingAuditor[u.id]}
                                className="h-7 text-xs bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                                {savingAuditor[u.id] ? <RefreshCw size={11} className="animate-spin" /> : 'Save'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ── Business Info Tab ─────────────────────────────────────── */}
        {isAdmin && (
          <TabsContent value="business" className="space-y-6">
            {/* ── Pricing & Capital ─────────────────────────────────────── */}
            <Card style={{ border: "1px solid var(--border-default)" }} data-testid="capital-costing-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Coins size={18} className="text-[#1A4D2E]" /> Pricing &amp; Capital
                </CardTitle>
                <p className="text-sm text-slate-500">
                  How the system values your capital (cost) each time a Purchase Order or Branch Transfer is received.
                  This drives below-capital warnings and your profit margins at the POS.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  {
                    value: 'last_purchase',
                    icon: TrendingUp,
                    label: 'Last Purchase / Transfer Cost',
                    desc: 'Capital always equals the most recent purchase or branch-transfer price. Simplest — reflects what you paid last.',
                  },
                  {
                    value: 'moving_average',
                    icon: Coins,
                    label: 'Moving Average',
                    desc: 'Capital is the weighted average of all stock on hand. Smooths out price swings — preferred by most accountants.',
                  },
                  {
                    value: 'smart',
                    icon: Zap,
                    label: 'Smart Pricing (recommended)',
                    desc: 'Automatic: if a new purchase is CHEAPER than current capital, switch to the moving average; if it is more EXPENSIVE, use the last purchase price. Protects margins both ways.',
                  },
                ].map((opt) => {
                  const active = (salesConfig.capital_costing_method || 'smart') === opt.value;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={savingSalesConfig}
                      onClick={() => saveCapitalMethod(opt.value)}
                      data-testid={`capital-method-${opt.value}`}
                      className={`w-full text-left rounded-xl border-2 p-4 transition-all flex items-start gap-3 ${
                        active ? 'border-[#1A4D2E] bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#1A4D2E] text-white' : 'bg-slate-100 text-slate-500'}`}>
                        <Icon size={18} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-semibold ${active ? 'text-[#1A4D2E]' : 'text-slate-700'}`}>{opt.label}</p>
                          {active && <CheckCircle2 size={15} className="text-[#1A4D2E]" />}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                      </div>
                    </button>
                  );
                })}
                <p className="text-xs text-slate-400 pt-1">
                  Note: a manual capital edit on an individual product always overrides this setting for that product.
                </p>
              </CardContent>
            </Card>

            {/* VAT Settings Card */}
            <Card style={{ border: "1px solid var(--border-default)" }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <FileCheck size={18} className="text-[#1A4D2E]" /> VAT Computation
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Enable or disable VAT (Value Added Tax) across all sales modes. Independent of BIR registration.
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4 max-w-2xl">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">
                      {salesConfig.vat_enabled !== false ? 'VAT Computation: Enabled' : 'VAT Computation: Disabled'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      {salesConfig.vat_enabled !== false
                        ? 'VAT badges and breakdown are shown on all sales. Products with "Vatable" class apply 12% VAT where applicable.'
                        : 'All VAT badges and breakdowns are hidden from the POS. All items are treated as VAT-exempt regardless of their product class or BIR registration status.'}
                    </p>
                    {salesConfig.vat_enabled === false && (
                      <p className="text-xs text-amber-600 mt-2 font-medium">
                        Note: VAT is hidden on the POS but product VAT classes are preserved in the database. Re-enabling will restore all badges.
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 pt-0.5">
                    <Switch
                      data-testid="vat-enabled-toggle"
                      checked={salesConfig.vat_enabled !== false}
                      onCheckedChange={saveVatEnabled}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cross-branch Capital Propagation */}
            <Card style={{ border: "1px solid var(--border-default)" }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <FileCheck size={18} className="text-[#1A4D2E]" /> Cross-Branch Capital Propagation
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Lets admins push a product's new PO capital (+ optional add-on) to other branches as a manual override, from the Price Change modal. Reversible in Price Manager → Propagation History.
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4 max-w-2xl">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">
                      {salesConfig.cross_branch_capital_propagation_enabled !== false ? 'Propagation: Enabled' : 'Propagation: Disabled'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      {salesConfig.cross_branch_capital_propagation_enabled !== false
                        ? 'Admins see "Compare & propagate to other branches" after receiving a PO. Each apply is gated by Admin PIN / Google Authenticator.'
                        : 'The propagation tool is hidden everywhere and the apply endpoint is blocked.'}
                    </p>
                  </div>
                  <div className="flex-shrink-0 pt-0.5">
                    <Switch
                      data-testid="capital-propagation-toggle"
                      checked={salesConfig.cross_branch_capital_propagation_enabled !== false}
                      onCheckedChange={saveCapitalPropagation}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card style={{ border: "1px solid var(--border-default)" }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Building2 size={18} className="text-[#1A4D2E]" /> Business Information
                </CardTitle>
                <p className="text-sm text-slate-500">Used on printed receipts, order slips, and trust receipts</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                  <div>
                    <Label className="text-xs text-slate-500">Business Name <span className="text-red-500">*</span></Label>
                    <Input data-testid="biz-name" value={bizInfo.business_name}
                      onChange={e => setBizInfo(b => ({ ...b, business_name: e.target.value }))}
                      placeholder="Your business name" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <Input data-testid="biz-phone" value={bizInfo.phone}
                      onChange={e => setBizInfo(b => ({ ...b, phone: e.target.value }))}
                      placeholder="(optional)" className="mt-1" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">Address</Label>
                    <Input data-testid="biz-address" value={bizInfo.address}
                      onChange={e => setBizInfo(b => ({ ...b, address: e.target.value }))}
                      placeholder="Street, City, Province (optional)" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">TIN (Tax ID)</Label>
                    <Input data-testid="biz-tin" value={bizInfo.tin}
                      onChange={e => setBizInfo(b => ({ ...b, tin: e.target.value }))}
                      placeholder="(optional)" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Receipt Footer Text</Label>
                    <Input data-testid="biz-footer" value={bizInfo.receipt_footer}
                      onChange={e => setBizInfo(b => ({ ...b, receipt_footer: e.target.value }))}
                      placeholder="This is not an official receipt." className="mt-1" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card style={{ border: "1px solid var(--border-default)" }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  Charge Agreement Terms
                </CardTitle>
                <p className="text-sm text-slate-500">Legal clause printed on Charge Agreements for credit sales. Use {'{business_name}'} as placeholder.</p>
              </CardHeader>
              <CardContent>
                <textarea
                  data-testid="biz-trust-terms"
                  value={bizInfo.trust_receipt_terms}
                  onChange={e => setBizInfo(b => ({ ...b, trust_receipt_terms: e.target.value }))}
                  rows={4}
                  className="w-full max-w-2xl border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 resize-none"
                  placeholder="Received the above item in good condition and in trust from {business_name}..."
                />

                {/* Copy Color Guide */}
                <div className="mt-4 max-w-2xl">
                  <Label className="text-xs text-slate-500">
                    Copy Color Guide
                    <span className="ml-1 text-slate-400 font-normal">(appears at bottom of Charge Agreement — Dot Matrix)</span>
                  </Label>
                  <Input
                    data-testid="biz-copy-color"
                    className="mt-1 text-sm"
                    value={bizInfo.copy_color_guide ?? ''}
                    onChange={e => setBizInfo(b => ({ ...b, copy_color_guide: e.target.value }))}
                    placeholder="Cash and Check — White and Pink  |  Credit — Pink  |  Store Copy — Yellow"
                  />
                  <p className="text-xs text-slate-400 mt-1">Leave blank to use the default text shown above.</p>
                </div>

                {/* Tax Disclaimer */}
                <div className="mt-3 max-w-2xl">
                  <Label className="text-xs text-slate-500">
                    Tax Disclaimer
                    <span className="ml-1 text-slate-400 font-normal">(printed at footer of Order Slips and Charge Agreements)</span>
                  </Label>
                  <textarea
                    data-testid="biz-tax-disclaimer"
                    value={bizInfo.tax_disclaimer ?? ''}
                    onChange={e => setBizInfo(b => ({ ...b, tax_disclaimer: e.target.value }))}
                    rows={2}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 resize-none"
                    placeholder="THIS DOCUMENT IS NOT VALID FOR CLAIMING INPUT TAX. THIS IS NOT AN OFFICIAL RECEIPT, PLEASE ASK FOR RECEIPT UPON PAYMENT."
                  />
                  <p className="text-xs text-slate-400 mt-1">Leave blank to use the default disclaimer shown above.</p>
                </div>

                {/* Default Print Format */}
                <div className="mt-4 max-w-2xl">
                  <Label className="text-xs text-slate-500">
                    Default Print Format
                    <span className="ml-1 text-slate-400 font-normal">(used when printing Order Slips, drafts, and sales receipts)</span>
                  </Label>
                  {/* "Full Page" was offered here until 2026-09-23 and did nothing:
                      the print path has rendered the dot-matrix layout for it since
                      Jul 2026, so the button promised A4/Letter and produced
                      continuous feed. An org that saved "full_page" earlier now
                      reads as Dot Matrix — which is the paper it was already
                      getting — and saving re-stamps it. */}
                  <div className="mt-1 flex gap-2">
                    {[
                      { value: 'dot_matrix', label: 'Dot Matrix', sub: 'Recommended — legal-size continuous feed' },
                      { value: 'thermal', label: 'Thermal', sub: '58mm/80mm receipt printer' },
                    ].map(opt => {
                      const current = (bizInfo.receipt_format === 'thermal') ? 'thermal' : 'dot_matrix';
                      return (
                      <button key={opt.value} type="button"
                        onClick={() => setBizInfo(b => ({ ...b, receipt_format: opt.value }))}
                        className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
                          current === opt.value
                            ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`receipt-format-${opt.value}`}
                      >
                        <p className={`text-sm font-semibold ${current === opt.value ? 'text-[#1A4D2E]' : 'text-slate-700'}`}>{opt.label}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{opt.sub}</p>
                      </button>
                      );
                    })}
                  </div>
                </div>

                {/* Company Logo */}
                <div className="mt-4 max-w-2xl">
                  <Label className="text-xs text-slate-500">
                    Company Logo
                    <span className="ml-1 text-slate-400 font-normal">(appears on left of Dot Matrix receipts — JPG, PNG, WEBP, max 1 MB)</span>
                  </Label>
                  <div className="mt-2 flex items-center gap-3">
                    {bizInfo.logo_url ? (
                      <div className="relative w-24 h-16 border rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center" style={{ borderColor: 'var(--border-default)' }}>
                        <img src={bizInfo.logo_url} alt="Logo preview" className="max-w-full max-h-full object-contain p-1" />
                      </div>
                    ) : (
                      <div className="w-24 h-16 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center text-slate-300 text-[10px] text-center p-1">
                        No logo
                      </div>
                    )}
                    <div className="flex flex-col gap-1.5">
                      <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml" className="hidden" onChange={handleLogoUpload} />
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo-btn">
                        {uploadingLogo ? <><RefreshCw size={12} className="animate-spin mr-1.5" />Uploading…</> : 'Upload Logo'}
                      </Button>
                      {bizInfo.logo_url && (
                        <Button size="sm" variant="outline" className="h-8 text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={handleLogoRemove} data-testid="remove-logo-btn">
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 flex-1">
                      Stored as a data image — works offline and never expires. Appears on the left side of Dot Matrix letterheads only — not on 58mm thermal.
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <Button onClick={saveBizInfo} disabled={savingBiz}
                    className="bg-[#1A4D2E] hover:bg-[#14532d] text-white" data-testid="save-biz-info">
                    <Save size={14} className="mr-1.5" /> {savingBiz ? 'Saving...' : 'Save Business Info'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* ── Timezone ────────────────────────────────────────────── */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Clock size={18} className="text-[#1A4D2E]" /> Organization Timezone
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Drives the SMS close-reminder schedule, the default sale date,
                  and any wall-clock formatting. Each branch under this
                  organization uses this timezone.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Timezone</label>
                    <select
                      data-testid="org-timezone-select"
                      value={tzValue}
                      onChange={e => setTzValue(e.target.value)}
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 bg-white">
                      {tzChoices.length === 0 && <option value={tzValue}>{tzValue}</option>}
                      {tzChoices.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </select>
                    {tzDetected && tzDetected !== tzValue && (
                      <p className="mt-1 text-[11px] text-amber-700">
                        <AlertTriangle size={11} className="inline mr-0.5" />
                        Your device is in <strong>{tzDetected}</strong> — pick
                        it from the list if your organization operates there.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Current time in selected zone</label>
                    <div data-testid="org-timezone-preview"
                      className="mt-1 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900">
                      {tzPreview}
                    </div>
                    <p className="mt-1 text-[11px] text-slate-400">
                      Updates every 30s. Scheduler picks up changes within 1 minute.
                    </p>
                  </div>
                </div>
                <div>
                  <Button onClick={saveTimezone} disabled={savingTz}
                    className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                    data-testid="save-org-timezone">
                    <Save size={14} className="mr-1.5" /> {savingTz ? 'Saving…' : 'Save Timezone'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* ── Feature Toggles ──────────────────────────────────────── */}
            <Card style={{ border: "1px solid var(--border-default)" }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <ToggleLeft size={18} className="text-[#1A4D2E]" /> Feature Toggles
                </CardTitle>
                <p className="text-sm text-slate-500">Enable or disable optional features based on your business needs</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-w-2xl">
                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid="feature-crop-credit-toggle">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Sprout size={16} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">Crop Credit</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Enable "Charged to Crop" credit type for agricultural businesses.
                          When disabled, credit sales use standard payment terms only.
                        </p>
                      </div>
                    </div>
                    <Switch
                      data-testid="crop-credit-switch"
                      checked={featureFlags.crop_credit_enabled}
                      onCheckedChange={(v) => toggleFeatureFlag('crop_credit_enabled', v)}
                      disabled={savingFlags}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid="feature-farm-expense-toggle">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Tractor size={16} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">Farm Expense</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Enable "Farm Expense" button on Expenses and Accounting pages.
                          When disabled, the farm expense option is hidden from all users.
                        </p>
                      </div>
                    </div>
                    <Switch
                      data-testid="farm-expense-switch"
                      checked={featureFlags.farm_expense_enabled}
                      onCheckedChange={(v) => toggleFeatureFlag('farm_expense_enabled', v)}
                      disabled={savingFlags}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── BIR Compliance ──────────────────────────────────────── */}
            {isAdmin && (
            <Card style={{ border: '1px solid var(--border-default)' }} data-testid="bir-config-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <FileCheck size={18} className="text-[#1A4D2E]" /> BIR Compliance
                </CardTitle>
                <p className="text-sm text-slate-500">Official BIR-accredited invoice issuance. Internal operations run normally while this is off.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Branch selector */}
                {(branches || []).length > 1 && (
                  <div>
                    <Label className="text-xs text-slate-500">Branch</Label>
                    <select
                      className="mt-1 w-full border rounded-md h-9 px-3 text-sm bg-white"
                      style={{ borderColor: 'var(--border-default)' }}
                      value={birBranch}
                      onChange={e => { setBirBranch(e.target.value); loadBirStatus(e.target.value); }}
                      data-testid="bir-branch-select"
                    >
                      {(branches || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                )}

                {!birLoadingBranch && (
                  <BirSetupStepper status={birStatus.bir_activation_status || 'not_applied'} />
                )}

                {birLoadingBranch ? (
                  <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
                    <RefreshCw size={14} className="animate-spin" /> Loading…
                  </div>
                ) : (() => {
                  const status = birStatus.bir_activation_status || 'not_applied';

                  // ── STATE: not_applied ────────────────────────────────────
                  if (status === 'not_applied') return (
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-50 border" style={{ borderColor: 'var(--border-default)' }}>
                        <Globe size={18} className="text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-700">BIR Compliance Not Activated</p>
                          <p className="text-xs text-slate-500 mt-1">
                            This branch operates in <strong>Internal Mode</strong> — Order Slips only, no official BIR receipts.
                            To issue Official Receipts or Sales Invoices, activate BIR Compliance below.
                          </p>
                        </div>
                      </div>
                      {!showBirApplyForm ? (
                        <Button data-testid="activate-bir-btn" onClick={() => setShowBirApplyForm(true)}
                          className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                          <FileCheck size={13} className="mr-1.5" /> Activate BIR Compliance
                        </Button>
                      ) : (
                        <div className="space-y-4 border rounded-xl p-4" style={{ borderColor: 'var(--border-default)' }}>
                          <p className="text-sm font-semibold text-slate-700">BIR Activation Application</p>
                          <p className="text-xs text-slate-500">Fill in your BIR registration details. Our team will verify and activate your compliance mode.</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[
                              { key: 'declared_business_name', label: 'Business Name (as BIR registered)', placeholder: 'Exact name on BIR COR' },
                              { key: 'declared_tin', label: 'TIN', placeholder: '000-000-000-000', mono: true },
                              { key: 'declared_atp_number', label: 'ATP Number', placeholder: 'Authority to Print No.' },
                              { key: 'declared_machine_serial', label: 'Machine Serial No. (MSN)', placeholder: 'BIR MSN' },
                              { key: 'declared_bir_permit_number', label: 'BIR Permit Number', placeholder: 'FP102016-XXXXX' },
                            ].map(f => (
                              <div key={f.key}>
                                <Label className="text-xs text-slate-500">{f.label}</Label>
                                <Input data-testid={`bir-apply-${f.key}`}
                                  className={`mt-1 h-9 text-sm ${f.mono ? 'font-mono' : ''}`}
                                  placeholder={f.placeholder}
                                  value={birApplyForm[f.key]}
                                  onChange={e => setBirApplyForm(p => ({ ...p, [f.key]: e.target.value }))}
                                />
                              </div>
                            ))}
                            <div>
                              <Label className="text-xs text-slate-500">VAT Registration</Label>
                              <select data-testid="bir-apply-vat-type"
                                className="mt-1 w-full border rounded-md h-9 px-3 text-sm bg-white"
                                style={{ borderColor: 'var(--border-default)' }}
                                value={birApplyForm.declared_vat_type}
                                onChange={e => setBirApplyForm(p => ({ ...p, declared_vat_type: e.target.value }))}>
                                <option value="non_vat">Non-VAT Registered</option>
                                <option value="vat_registered">VAT Registered (12%)</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Receipt Workflow</Label>
                            <div className="mt-2 space-y-2">
                              {[
                                { value: 'direct', label: 'Mode 1 — Direct', desc: 'Official invoice issued immediately at payment.' },
                                { value: 'staged', label: 'Mode 2 — Staged (Recommended)', desc: 'Order Slip first, issue invoice after stocks confirmed.' },
                                { value: 'smart_role', label: 'Mode 3 — Smart Role', desc: 'Only designated billing users can issue official invoices.' },
                              ].map(opt => (
                                <label key={opt.value} className={`flex items-start gap-3 p-2.5 border rounded-lg cursor-pointer text-sm ${birApplyForm.declared_receipt_workflow === opt.value ? 'border-[#1A4D2E] bg-emerald-50' : 'border-slate-200'}`}>
                                  <input type="radio" value={opt.value} checked={birApplyForm.declared_receipt_workflow === opt.value}
                                    onChange={e => setBirApplyForm(p => ({ ...p, declared_receipt_workflow: e.target.value }))}
                                    className="mt-0.5 accent-[#1A4D2E]" />
                                  <div><p className="font-medium">{opt.label}</p><p className="text-xs text-slate-500">{opt.desc}</p></div>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center justify-between p-3 border rounded-lg" style={{ borderColor: 'var(--border-default)' }}>
                            <div>
                              <p className="text-sm font-medium">Senior Citizen / PWD Discount</p>
                              <p className="text-xs text-slate-500">For pharmacy and grocery businesses.</p>
                            </div>
                            <Switch checked={birApplyForm.senior_citizen_discount_enabled}
                              onCheckedChange={v => setBirApplyForm(p => ({ ...p, senior_citizen_discount_enabled: v }))} />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Notes for reviewer (optional)</Label>
                            <Input className="mt-1 h-9 text-sm" placeholder="Any additional context..."
                              value={birApplyForm.applicant_notes}
                              onChange={e => setBirApplyForm(p => ({ ...p, applicant_notes: e.target.value }))} />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setShowBirApplyForm(false)} disabled={submittingBir}>Cancel</Button>
                            <Button data-testid="submit-bir-application-btn"
                              onClick={submitBirApplication} disabled={submittingBir || !birApplyForm.declared_tin.trim()}
                              className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                              {submittingBir ? <><RefreshCw size={13} className="animate-spin mr-1.5" />Submitting…</> : <><FileCheck size={13} className="mr-1.5" />Submit Application</>}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );

                  // ── STATE: pending_review ─────────────────────────────────
                  if (status === 'pending_review') return (
                    <PendingBIRSection
                      birBranch={birBranch}
                      withdrawBirApplication={withdrawBirApplication}
                    />
                  );

                  // ── STATE: active ─────────────────────────────────────────
                  if (status === 'active' || status === 'update_pending') return (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                        <FileCheck size={16} className="text-emerald-600 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-emerald-800">BIR Compliance Active</p>
                          {birStatus.activated_at && (
                            <p className="text-xs text-emerald-700">
                              Activated {new Date(birStatus.activated_at).toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                        {status === 'update_pending' && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">Update pending</span>
                        )}
                      </div>
                      {/* Read-only BIR info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        {[
                          { label: 'Business Name (BIR)', value: birStatus.business_name_bir },
                          { label: 'TIN', value: birStatus.tin, mono: true },
                          { label: 'ATP Number', value: birStatus.atp_number },
                          { label: 'Machine Serial (MSN)', value: birStatus.machine_serial },
                          { label: 'BIR Permit Number', value: birStatus.bir_permit_number },
                          { label: 'VAT Type', value: birStatus.vat_type === 'vat_registered' ? 'VAT Registered (12%)' : 'Non-VAT' },
                          { label: 'Receipt Workflow', value: birStatus.receipt_workflow === 'direct' ? 'Mode 1 — Direct' : birStatus.receipt_workflow === 'staged' ? 'Mode 2 — Staged' : 'Mode 3 — Smart Role' },
                        ].map(f => (
                          <div key={f.label} className="p-3 rounded-lg bg-slate-50 border" style={{ borderColor: 'var(--border-default)' }}>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">{f.label}</p>
                            <p className={`text-sm font-medium text-slate-700 ${f.mono ? 'font-mono' : ''}`}>{f.value || '—'}</p>
                          </div>
                        ))}
                      </div>

                      {/* Address / Phone override — editable in active state */}
                      <div className="space-y-3 pt-2">
                        <div>
                          <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mb-1">
                            OR Header — Address &amp; Phone
                          </p>
                          <p className="text-xs text-slate-400 mb-3">
                            Leave blank to automatically use the branch address/phone on the official receipt.
                            Fill in only if the BIR-registered address differs from the branch address.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-slate-500">
                                Address Override
                                {!birStatus.bir_address && birStatus.branch_address && (
                                  <span className="ml-1 text-emerald-600">(auto: {birStatus.branch_address.slice(0, 30)}{birStatus.branch_address.length > 30 ? '…' : ''})</span>
                                )}
                              </Label>
                              <Input
                                data-testid="bir-address-override"
                                className="mt-1 h-9 text-sm"
                                placeholder={birStatus.branch_address || 'Branch address (auto)'}
                                value={birStatus.bir_address || ''}
                                onChange={e => setBirStatus(s => ({ ...s, bir_address: e.target.value }))}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">
                                Phone Override
                                {!birStatus.bir_phone && birStatus.branch_phone && (
                                  <span className="ml-1 text-emerald-600">(auto: {birStatus.branch_phone})</span>
                                )}
                              </Label>
                              <Input
                                data-testid="bir-phone-override"
                                className="mt-1 h-9 text-sm"
                                placeholder={birStatus.branch_phone || 'Branch phone (auto)'}
                                value={birStatus.bir_phone || ''}
                                onChange={e => setBirStatus(s => ({ ...s, bir_phone: e.target.value }))}
                              />
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <p className="text-xs text-slate-500 flex-1">
                              Effective on receipt:&nbsp;
                              <strong className="text-slate-700">
                                {birStatus.effective_address || birStatus.branch_address || '(not set)'}
                              </strong>
                            </p>
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              data-testid="save-bir-address-btn"
                              onClick={async () => {
                                try {
                                  const res = await api.put('/bir/branch-toggles', { branch_id: birBranch, bir_address: birStatus.bir_address || '', bir_phone: birStatus.bir_phone || '' });
                                  setBirStatus(prev => ({ ...prev, ...res.data }));
                                  toast.success('Address settings saved');
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                              }}>
                              <Save size={11} className="mr-1" /> Save
                            </Button>
                          </div>
                        </div>
                      </div>

                      <p className="text-xs text-slate-400">To update TIN, ATP, MSN or Business Name, contact support or submit a document update.</p>

                      {/* ── Step 2b: Receipt Workflow mode (owner-switchable per
                          branch, 2026-07-29 owner decision). Strict-mode
                          (accredited) orgs are LOCKED to Direct — the always-
                          issue promise BIR verified must never be switchable. ── */}
                      <div className="pt-2" data-testid="bir-workflow-mode">
                        <div className="flex items-center gap-2 mb-1">
                          <Zap size={16} className="text-[#1A4D2E]" />
                          <p className="text-sm font-semibold text-slate-800">Receipt Workflow — this branch</p>
                        </div>
                        {birOps?.bir_strict_mode ? (
                          <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 p-3" data-testid="bir-workflow-strict-lock">
                            <p className="text-xs font-semibold text-[#1A4D2E]">Mode 1 — Direct · LOCKED (BIR Accredited)</p>
                            <p className="text-[11px] text-slate-600 mt-1">
                              This account operates in full BIR accreditation mode: every checkout
                              immediately issues the official Sales / Charge Invoice. The workflow
                              cannot be changed.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {[
                              { value: 'direct', label: 'Mode 1 — Direct', desc: 'Every checkout immediately issues and prints the official Sales / Charge Invoice. No order-slip step.' },
                              { value: 'staged', label: 'Mode 2 — Staged', desc: 'Checkout prints an Order Slip first — prepare the order, correct quantities if stock is short, then Generate Invoice bills what was actually delivered.' },
                              { value: 'smart_role', label: 'Mode 3 — Smart Role', desc: 'Only designated billing users can issue official invoices.' },
                            ].map(opt => {
                              const current = (birOps?.receipt_workflow || 'staged') === opt.value;
                              return (
                                <button key={opt.value} type="button"
                                  data-testid={`bir-workflow-${opt.value}`}
                                  disabled={birOpsSavingKey === 'receipt_workflow'}
                                  onClick={() => {
                                    if (current) return;
                                    const ok = window.confirm(
                                      `Switch this branch to ${opt.label}?\n\n${opt.desc}\n\nThis changes checkout behavior immediately and is recorded in the audit log.`
                                    );
                                    if (!ok) return;
                                    saveBirOpsToggle({ receipt_workflow: opt.value }, 'receipt_workflow')
                                      .then(() => {
                                        setBirStatus(s => ({ ...s, receipt_workflow: opt.value }));
                                        toast.success(`Receipt workflow: ${opt.label}`);
                                      })
                                      .catch(() => {});
                                  }}
                                  className={`w-full text-left flex items-start gap-3 p-2.5 border rounded-lg text-sm transition-colors ${current ? 'border-[#1A4D2E] bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                                  <span className={`mt-1 inline-block w-3 h-3 rounded-full border-2 shrink-0 ${current ? 'border-[#1A4D2E] bg-[#1A4D2E]' : 'border-slate-300'}`} />
                                  <span>
                                    <span className="font-medium block">{opt.label}{current ? ' · active' : ''}</span>
                                    <span className="text-xs text-slate-500">{opt.desc}</span>
                                  </span>
                                </button>
                              );
                            })}
                            <p className="text-[11px] text-slate-400">
                              Per-branch setting. Order Slips always print "Not an Official Invoice";
                              pending sales stay listed under Generate Invoice until billed or voided.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* ── Step 3: Document printing & POS behavior (2026-07-29:
                          consolidated back here — one BIR section end to end) ── */}
                      <div className="pt-2" data-testid="bir-step3-printing">
                        <div className="flex items-center gap-2 mb-1">
                          <FileCheck size={16} className="text-[#1A4D2E]" />
                          <p className="text-sm font-semibold text-slate-800">Step 3 — Document Printing &amp; POS</p>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">How Sales / Charge Invoices print and whether VAT can be changed at the POS — for this branch. (SC/PWD discount stays under Branch Settings.)</p>
                        <BirPrintingPanel bir={birOps} savingKey={birOpsSavingKey} onToggle={saveBirOpsToggle} />
                      </div>

                      {/* ── Step 4: Reports ── */}
                      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 flex items-start gap-3" data-testid="bir-step4-reports">
                        <FileCheck size={18} className="text-slate-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-700">Step 4 — Registers &amp; Reports</p>
                          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                            Issued invoices, the sequential SI/CI registers, Z-reports and the sales book live in <strong>BIR Compliance Reports</strong>.
                          </p>
                          <Button size="sm" variant="outline" className="mt-2.5 h-8 text-xs"
                            data-testid="goto-bir-reports-btn"
                            onClick={() => window.location.assign('/bir-compliance')}>
                            Open BIR Compliance Reports
                          </Button>
                        </div>
                      </div>
                    </div>
                  );

                  return null;
                })()}
              </CardContent>
            </Card>
            )}

            {/* ── Danger Zone ─────────────────────────────────────────── */}
            {currentUser?.organization_id && (
            <Card className="border-red-200 bg-red-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-red-700" style={{ fontFamily: 'Manrope' }}>
                  <AlertTriangle size={18} className="text-red-600" /> Danger Zone
                </CardTitle>
                <p className="text-sm text-red-600/80">
                  Irreversible actions. Proceed with caution.
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4 p-4 border border-red-200 rounded-lg bg-white">
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">Reset Company Data</p>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-sm">
                      Wipe all customers, invoices, sales, payments, employees, and inventory back to zero.
                      A backup is automatically created before reset. Your owner account is preserved.
                    </p>
                  </div>
                  <Button
                    data-testid="open-reset-company-btn"
                    onClick={() => setShowResetModal(true)}
                    variant="outline"
                    size="sm"
                    className="shrink-0 border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
                  >
                    <Trash2 size={13} className="mr-1.5" /> Reset Company
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
          </TabsContent>
        )}

        {/* ── Connect Terminal Tab ───────────────────────────────────── */}
        {/* ── Branch Settings Tab ────────────────────────────────────── */}
        {isAdmin && (
          <TabsContent value="branch" className="space-y-6" data-testid="branch-settings-content">
            {/* Prominent active-branch banner — prevents editing the wrong branch */}
            <div className="rounded-xl border-2 border-[#1A4D2E] bg-[#1A4D2E]/[0.06] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              data-testid="branch-settings-banner">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-lg bg-[#1A4D2E] text-white flex items-center justify-center shrink-0">
                  <Store size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-[#1A4D2E]/70 font-semibold">You are editing settings for</p>
                  <p className="text-lg font-bold text-[#1A4D2E] truncate" data-testid="branch-settings-active-name">{branchSelName}</p>
                </div>
              </div>
              {(branches || []).length > 1 && (
                <div className="flex items-center gap-2 shrink-0">
                  <Label className="text-xs text-slate-600">Switch branch</Label>
                  <select
                    value={branchSel}
                    onChange={e => setBranchSel(e.target.value)}
                    className="h-9 px-2 pr-7 text-sm border-2 border-[#1A4D2E]/40 rounded-md bg-white font-medium"
                    data-testid="branch-settings-selector">
                    {(branches || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {(branches || []).length > 1 && (
              <p className="text-xs text-amber-600 flex items-center gap-1.5 -mt-3">
                <AlertTriangle size={13} /> Changes below apply ONLY to <span className="font-semibold">{branchSelName}</span>. Double-check the branch name above before saving.
              </p>
            )}

            {/* Tax & Discounts card */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <Percent size={18} className="text-[#1A4D2E]" />
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>Tax &amp; Discounts</h3>
              </div>
              <p className="text-sm text-slate-500 mb-5">VAT and Senior Citizen / PWD settings for this branch.</p>

              {branchBirLoading ? (
                <div className="flex items-center text-slate-400 text-sm py-6"><RefreshCw size={16} className="animate-spin mr-2" /> Loading branch settings…</div>
              ) : (
                <div className="space-y-4">
                  {/* VAT registration status (read-only — managed by BIR registration) */}
                  <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="min-w-0 pr-4">
                      <p className="text-sm font-medium text-slate-800">VAT Registration</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Whether this branch charges VAT. Set during BIR registration (Business Info tab).
                      </p>
                    </div>
                    <Badge className={branchBir?.vat_type === 'vat_registered'
                      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                      : 'bg-slate-200 text-slate-600 border-slate-300'}
                      data-testid="branch-vat-status">
                      {branchBir?.vat_type === 'vat_registered' ? `VAT Registered (${taxConfig.vat_rate}%)` : 'Non-VAT'}
                    </Badge>
                  </div>

                  {/* Platform VAT rate (read-only) */}
                  <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                    <div className="min-w-0 pr-4">
                      <p className="text-sm font-medium text-slate-800">Standard VAT Rate</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        National rate set by the platform (Philippine law). Receipts &amp; BIR reports use this automatically.
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-2xl font-bold text-[#1A4D2E]" style={{ fontVariantNumeric: 'tabular-nums' }}>{taxConfig.vat_rate}%</span>
                      <Lock size={13} className="text-slate-300" />
                    </div>
                  </div>

                  {/* SC/PWD toggle (live, per branch) */}
                  <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                    <div className="min-w-0 pr-4">
                      <p className="text-sm font-medium text-slate-800">Senior Citizen / PWD Discount</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        When ON, cashiers can mark a sale as SC/PWD at checkout — VAT is removed and a{' '}
                        <span className="font-semibold text-[#1A4D2E]">{taxConfig.scpwd_discount_percent}%</span> discount is applied (per RA 9994 / RA 10754).
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {savingScpwd && <RefreshCw size={13} className="animate-spin text-slate-400" />}
                      <Switch
                        checked={!!branchBir?.senior_citizen_discount_enabled}
                        onCheckedChange={saveScpwdEnabled}
                        disabled={savingScpwd || !branchSel}
                        data-testid="branch-scpwd-toggle" />
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-400 flex items-center gap-1.5 pt-1">
                    <Lock size={11} /> The VAT rate and SC/PWD discount % are legal constants — adjustable only by the platform administrator.
                  </p>
                </div>
              )}
            </Card>

            {/* BIR printing settings consolidated into the BIR Compliance tab
                (2026-07-29, owner ask: one BIR section end to end). Pointer only. */}
            {!branchBirLoading && branchBir?.bir_mode_enabled && (
              <Card className="p-5" data-testid="branch-bir-printing-pointer">
                <div className="flex items-start gap-3">
                  <FileCheck size={18} className="text-[#1A4D2E] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">BIR Document Printing has moved</p>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      SI/CI auto-print and the POS VAT-override toggle now live with the rest of your
                      BIR setup under the <strong>BIR Compliance</strong> tab (Step 3). The SC/PWD
                      discount toggle stays here in Branch Settings.
                    </p>
                    <Button size="sm" variant="outline"
                      className="mt-2.5 h-8 text-xs border-[#1A4D2E]/40 text-[#1A4D2E] hover:bg-[#1A4D2E]/10"
                      data-testid="goto-bir-tab-btn"
                      onClick={() => { setBirBranch(branchSel); setActiveSettingsTab('business'); }}>
                      <FileCheck size={13} className="mr-1.5" /> Open BIR Compliance
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </TabsContent>
        )}

        {/* ── Connect Terminal Tab ───────────────────────────────────── */}
        {isAdmin && (
          <TabsContent value="terminal" className="space-y-6">
            <ConnectTerminalPanel branches={branches} />
          </TabsContent>
        )}

        {/* ── Telegram Tab ───────────────────────────────────────────── */}
        {isAdmin && (
          <TabsContent value="telegram" className="space-y-6">
            <TelegramSettingsTab branches={branches || []} />
          </TabsContent>
        )}

        {/* ── Multi-Cashier Shifts Tab ───────────────────────────────── */}
        {isAdmin && (
          <TabsContent value="shifts" className="space-y-6">
            <ShiftsSettingsPanel />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="subscription" className="space-y-6">
            <SubscriptionSettings branches={branches} />
          </TabsContent>
        )}
      </Tabs>

      {/* ── Reset Company Modal ──────────────────────────────────────── */}
      <ResetCompanyModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        orgId={currentUser?.organization_id}
        companyName={bizInfo.business_name || 'Company'}
      />
    </div>
  );
}
